export type ScheduleType = "interval" | "daily" | "weekly" | "monthly";

export interface ScheduleState {
  type: ScheduleType;
  intervalUnit: "minutes" | "hours";
  intervalValue: number;
  hour: string;
  minute: string;
  days: string[];
  monthDay: string;
}

export const MINUTE_INTERVALS = [1, 2, 5, 10, 15, 30];
export const HOUR_INTERVALS = [1, 2, 3, 4, 6, 8, 12];
export const MINUTE_OPTIONS = [0, 15, 30, 45];
export const MAX_MONTH_DAY = 28;

export const DEFAULT_SCHEDULE: ScheduleState = {
  type: "daily",
  intervalUnit: "minutes",
  intervalValue: 5,
  hour: "9",
  minute: "0",
  days: ["1", "2", "3", "4", "5"],
  monthDay: "1",
};

export function buildCron(s: ScheduleState): string {
  switch (s.type) {
    case "interval":
      return s.intervalUnit === "minutes"
        ? `*/${s.intervalValue} * * * *`
        : `0 */${s.intervalValue} * * *`;
    case "daily":
      return `${s.minute} ${s.hour} * * *`;
    case "weekly":
      return `${s.minute} ${s.hour} * * ${[...s.days].sort().join(",")}`;
    case "monthly":
      return `${s.minute} ${s.hour} ${s.monthDay} * *`;
  }
}

const isIntIn = (value: string, min: number, max: number) =>
  /^\d+$/.test(value) &&
  String(Number(value)) === value &&
  Number(value) >= min &&
  Number(value) <= max;

function parseInterval(
  unit: ScheduleState["intervalUnit"],
  step: string,
  allowed: number[],
): ScheduleState | null {
  const intervalValue = Number(step.slice(2));
  if (!allowed.includes(intervalValue)) return null;
  return { ...DEFAULT_SCHEDULE, type: "interval", intervalUnit: unit, intervalValue };
}

// Null means the scheduler controls can't show this expression exactly, so it must stay as raw cron.
export function parseCron(cron: string): ScheduleState | null {
  const trimmed = (cron ?? "").trim();
  const parts = trimmed.split(/\s+/);
  if (parts.length !== 5) return null;
  const [minute, hour, day, month, weekday] = parts;
  if (month !== "*") return null;

  let parsed: ScheduleState | null = null;
  const everyDay = day === "*" && weekday === "*";

  if (everyDay && minute.startsWith("*/") && hour === "*") {
    parsed = parseInterval("minutes", minute, MINUTE_INTERVALS);
  } else if (everyDay && minute === "0" && hour.startsWith("*/")) {
    parsed = parseInterval("hours", hour, HOUR_INTERVALS);
  } else if (
    MINUTE_OPTIONS.map(String).includes(minute) &&
    isIntIn(hour, 0, 23)
  ) {
    if (everyDay) {
      parsed = { ...DEFAULT_SCHEDULE, type: "daily", minute, hour };
    } else if (weekday === "*" && isIntIn(day, 1, MAX_MONTH_DAY)) {
      parsed = { ...DEFAULT_SCHEDULE, type: "monthly", minute, hour, monthDay: day };
    } else if (day === "*" && weekday.split(",").every((d) => /^[0-6]$/.test(d))) {
      parsed = { ...DEFAULT_SCHEDULE, type: "weekly", minute, hour, days: weekday.split(",") };
    }
  }

  return parsed && buildCron(parsed) === trimmed ? parsed : null;
}
